import { Injectable, inject } from '@angular/core';
import { httpResource, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { AuthCodeData, AuthErrorResponse, AuthSuccessResponse, PasswordAuthResult, RegisterAuthResult, UserDto } from '@mb/auth';
import type { ApiResponse } from '@mb/common';
import { z } from 'zod';
import { isBrowser } from '@mb/common/client';

/**
 * AuthHttpService
 *
 * - **Session / refresh**: `httpResource` for reactive GET/POST reads tied to the app lifecycle.
 * - **Login, register, password flows**: `HttpClient.post` via explicit methods (mutations are not
 *   recreated as ad hoc `httpResource` factories — avoids NG0203 and polling in the store).
 *
 * @see https://www.angulararchitects.io/blog/using-the-resource-api-with-the-ngrx-signal-store/
 */

export interface Session {
    user: UserDto;
    sessionId: string;
    needsRefresh: boolean;
    isValid: boolean;
    expires: string;
    roles: string[];
    permissions: string[];
    preferences: Record<string, any>;
    lastLogin: string;
    profileComplete: boolean;
    accountStatus: string;
}

export type SessionResponse = ApiResponse<Session>;
export type CsrfResponse = ApiResponse<{ csrfToken: string }>;

const UserDtoSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    image: z.union([z.string().nullable(), z.undefined()]),
    systemRole: z.string(),
    language: z.string().nullish(),
    emailVerified: z.union([z.string(), z.null(), z.undefined()]),
    createdAt: z.union([z.string(), z.undefined()]),
    updatedAt: z.union([z.string(), z.null(), z.undefined()]),
});

const TokenPairSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    accessExpiresAt: z.string(),
    refreshExpiresAt: z.string(),
    sessionId: z.string(),
});

const SessionSchema = z.object({
    user: UserDtoSchema,
    sessionId: z.string(),
    needsRefresh: z.boolean(),
    isValid: z.boolean(),
    expires: z.string(),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
    preferences: z.record(z.string(), z.unknown()),
    lastLogin: z.string(),
    profileComplete: z.boolean(),
    accountStatus: z.string(),
});

/** Zod 4: use `.optional()` for absent keys — `z.union([t, z.undefined()])` still requires the key. */
const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
    z.object({
        success: z.boolean(),
        data: dataSchema.nullish().optional(),
        message: z.string().optional(),
        error: z.string().optional(),
    });

const SessionErrorBodySchema = z.object({
    error: z.string(),
});

const SessionResponseSchema = z.union([
    ApiResponseSchema(SessionSchema),
    SessionErrorBodySchema,
]);

const TokenResponseSchema = ApiResponseSchema(TokenPairSchema);

function parseSessionResponse(response: unknown): Session | null {
    const parsed = SessionResponseSchema.safeParse(response);
    if (!parsed.success) {
        return null;
    }

    const body = parsed.data;
    if (!('success' in body) || !body.success || !body.data) {
        return null;
    }

    const session = body.data;
    return {
        ...session,
        user: {
            ...session.user,
            language: session.user.language ?? null,
            emailVerified: session.user.emailVerified
                ? new Date(session.user.emailVerified)
                : null,
            createdAt: session.user.createdAt ? new Date(session.user.createdAt) : new Date(),
            updatedAt: session.user.updatedAt ? new Date(session.user.updatedAt) : null,
        },
    } as Session;
}

@Injectable({
    providedIn: 'root'
})
export class AuthHttpService {
    private readonly baseUrl = '/api/auth';
    private readonly http = inject(HttpClient);

    private sessionResource = httpResource(() => {
        if (!isBrowser()) {
            return undefined;
        }
        return {
            url: `${this.baseUrl}/session`,
            method: 'GET',
            withCredentials: true
        };
    }, {
        parse: (response) => parseSessionResponse(response),
    });

    private refreshRequested = false;

    private refreshResource = httpResource(() => {
        if (!isBrowser() || !this.refreshRequested) {
            return undefined;
        }
        return {
            url: `${this.baseUrl}/refresh`,
            method: 'POST',
            withCredentials: true,
        };
    }, {
        parse: (response) => {
            const parsed = TokenResponseSchema.safeParse(response);
            if (!parsed.success) {
                return null;
            }
            return parsed.data.success ? parsed.data.data ?? null : null;
        },
    });

    // —— Mutations (HttpClient; no dynamic httpResource factories) ——

    login(email: string, password: string): Promise<PasswordAuthResult> {
        return firstValueFrom(
            this.http.post<PasswordAuthResult>(`${this.baseUrl}/login`, { email, password }, { withCredentials: true }),
        );
    }

    register(email: string, password: string, name?: string): Promise<RegisterAuthResult> {
        return firstValueFrom(
            this.http.post<RegisterAuthResult>(
                `${this.baseUrl}/register`,
                { email, password, name: name ?? '' },
                { withCredentials: true },
            ),
        );
    }

    exchangeAuthCode(code: string): Promise<ApiResponse<AuthCodeData> | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<ApiResponse<AuthCodeData> | AuthErrorResponse>(
                `${this.baseUrl}/exchange-code`,
                { code },
                { withCredentials: true },
            ),
        );
    }

    requestPasswordReset(email: string): Promise<AuthSuccessResponse | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<AuthSuccessResponse | AuthErrorResponse>(
                `${this.baseUrl}/forgot-password`,
                { email },
                { withCredentials: true },
            ),
        );
    }

    confirmPasswordReset(
        email: string,
        resetToken: string,
        newPassword: string,
    ): Promise<AuthSuccessResponse | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<AuthSuccessResponse | AuthErrorResponse>(
                `${this.baseUrl}/reset-password`,
                { email, resetToken, newPassword },
                { withCredentials: true },
            ),
        );
    }

    changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<AuthSuccessResponse> | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<ApiResponse<AuthSuccessResponse> | AuthErrorResponse>(
                `${this.baseUrl}/change-password`,
                { currentPassword, newPassword },
                { withCredentials: true },
            ),
        );
    }

    /** Stores the signed-in account's interface language. Not under `baseUrl`: it is account data, not an auth exchange. */
    updateLanguage(language: string): Promise<ApiResponse<{ language: string }>> {
        return firstValueFrom(
            this.http.patch<ApiResponse<{ language: string }>>(
                '/api/v1/users/me/language',
                { language },
                { withCredentials: true },
            ),
        );
    }

    verifyEmail(token: string, email: string): Promise<AuthSuccessResponse | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<AuthSuccessResponse | AuthErrorResponse>(
                `${this.baseUrl}/verify-email`,
                { token, email },
                { withCredentials: true },
            ),
        );
    }

    resendVerification(email: string): Promise<AuthSuccessResponse | AuthErrorResponse> {
        return firstValueFrom(
            this.http.post<AuthSuccessResponse | AuthErrorResponse>(
                `${this.baseUrl}/resend-verification`,
                { email },
                { withCredentials: true },
            ),
        );
    }

    get sessionResourceAccessor() {
        return this.sessionResource;
    }

    get refreshResourceAccessor() {
        return this.refreshResource;
    }

    reloadSession() {
        this.sessionResource.reload();
    }

    reloadRefreshToken() {
        this.refreshRequested = true;
        this.refreshResource.reload();
    }
}
